// Where a finished game goes, and how the account's online games come back.
// A signed-in player's online game is the server's record: it is queued in
// the outbox and sent, never written to `localStorage`. Everything else
// (bot, hot-seat, an anonymous online game) is saved on this device.
import { PendingSyncStore, commitFinishedRecord } from '@bn-games/history-core';
import { getAuthSnapshot } from '@bn-games/auth-client';
import { fetchGameHistory, getStats, recordMatch } from '@bn-games/game-history-client';
import type { GameStats, MatchOutcome, MatchRecord } from '@bn-games/game-history-client';
import { isTermination } from '../game/outcome.js';
import { isVariantId } from '../game/rules/index.js';
import type { Color } from '../game/types.js';
import { historyStore } from './HistoryStore.js';
import { recordOutcome, recordState } from './record.js';
import { HISTORY_SCHEMA_VERSION } from './types.js';
import type { GameRecord, RecordedMove, RecordedResult, RecordedSeed } from './types.js';

const outbox = new PendingSyncStore<GameRecord>({ storageKey: 'chess-pending-sync' });

/** The result from the recording player's side, or null for a record with no side (hot-seat). */
export function matchOutcome(record: GameRecord): MatchOutcome | null {
  const mine = record.seed.localColor;
  if (mine === null) return null;
  const { winner } = recordOutcome(record);
  if (winner === null) return 'draw';
  return winner === mine ? 'win' : 'loss';
}

/**
 * Send one finished online record. True means "do not retry": delivered, or
 * a record no retry could ever deliver.
 */
export async function syncOnlineRecord(record: GameRecord): Promise<boolean> {
  const outcome = matchOutcome(record);
  if (outcome === null) return true;
  const result = await recordMatch('chess', {
    id: record.id,
    variant: record.variant,
    outcome,
    players: record.players,
    // The game's own facts ride in the seed: the wire has no fields for them.
    seed: { ...record.seed, startFen: record.startFen, termination: record.termination },
    moves: record.moves,
    createdAt: record.createdAt,
    finishedAt: record.finishedAt,
  });
  return result.ok;
}

/** Route a finished game to this device or to the account. Call it once per game. */
export function commitRecord(record: GameRecord): void {
  const { authState, userId } = getAuthSnapshot();
  commitFinishedRecord({
    record,
    accountId: userId ?? '',
    isOnlineMatch: record.mode === 'online',
    signedIn: authState === 'signed-in',
    saveLocal: (r) => historyStore.add(r),
    syncRemote: syncOnlineRecord,
    outbox,
  });
}

/** Retry whatever is still queued for the signed-in account. A no-op otherwise. */
export function flushOutbox(): void {
  const { authState, userId } = getAuthSnapshot();
  if (authState !== 'signed-in' || userId === null) return;
  void outbox.flush(userId, syncOnlineRecord);
}

function resultFor(outcome: MatchOutcome, mine: Color): RecordedResult | null {
  if (outcome === 'draw') return '1/2-1/2';
  if (outcome === 'abandoned') return null;
  const whiteWon = (outcome === 'win') === (mine === 'w');
  return whiteWon ? '1-0' : '0-1';
}

function isPlayers(value: unknown): value is GameRecord['players'] {
  if (typeof value !== 'object' || value === null) return false;
  const players = value as Record<string, unknown>;
  return typeof players['white'] === 'string' && typeof players['black'] === 'string';
}

function isRecordedMove(value: unknown): value is RecordedMove {
  if (typeof value !== 'object' || value === null) return false;
  const move = value as Record<string, unknown>;
  if (typeof move['san'] !== 'string' || typeof move['uci'] !== 'string') return false;
  const clocks = move['clocks'];
  if (clocks === null) return true;
  if (typeof clocks !== 'object' || clocks === undefined) return false;
  const reading = clocks as Record<string, unknown>;
  return Number.isFinite(reading['w']) && Number.isFinite(reading['b']);
}

interface RemoteSeed extends RecordedSeed {
  readonly startFen?: unknown;
  readonly termination?: unknown;
}

/** A server row back into a record; null for anything this client cannot replay. */
export function toRemoteGameRecord(remote: MatchRecord): GameRecord | null {
  if (!isVariantId(remote.variant) || !isPlayers(remote.players)) return null;
  if (!Array.isArray(remote.moves) || !remote.moves.every(isRecordedMove)) return null;
  if (typeof remote.seed !== 'object' || remote.seed === null) return null;
  const seed = remote.seed as RemoteSeed;
  if (seed.localColor !== 'w' && seed.localColor !== 'b') return null;
  if (typeof seed.startFen !== 'string' || !isTermination(seed.termination)) return null;
  const result = resultFor(remote.outcome, seed.localColor);
  if (result === null) return null;
  const record: GameRecord = {
    id: remote.id,
    version: HISTORY_SCHEMA_VERSION,
    createdAt: remote.createdAt,
    finishedAt: remote.finishedAt,
    mode: 'online',
    variant: remote.variant,
    players: remote.players,
    seed,
    startFen: seed.startFen,
    moves: remote.moves,
    result,
    termination: seed.termination,
  };
  // The list opens records in the replay: one that does not replay is not listed.
  return recordState(record) === null ? null : record;
}

export function fetchOnlineHistory(): Promise<GameRecord[]> {
  return fetchGameHistory('chess', toRemoteGameRecord);
}

/** Chess's slice of the account stats; null on any failure. */
export async function fetchChessStats(): Promise<GameStats | null> {
  const result = await getStats();
  return result.ok ? result.value.byGame.chess : null;
}
